import React, {useEffect, useState, useTransition} from 'react';
import {InternalPageRoot, ThemedButton, ThemedIconButton} from "../index";
import {useTheme} from "../../common/ProjectThemeProvider";
import {
    checkNullArr,
    checkNullJson,
    checkNullStr,
    copyTextToClipboard,
    dropArrDuplicates,
    dropArrEmptyValues,
    getJsonValue,
    getJsonValueFromNestedKeys,
    getUrlMainDomain,
    isBoolTrue,
    isJsonValueTrue,
    limitStringWords
} from "../../common/fusion-ui/utils/ReactUtils";
import {formatDateValue, getSubdomainSessionData, printError, printLog, sendRequest} from "../utils/AppUtils";
import {useToast} from "../../common/fusion-ui/provider/ToastProvider";
import {Keys, ReqUrls, ShowViewType} from "../config/AppConfig";
import {CssVariant, DefStrNA, IconClass} from '../../common/fusion-ui/config/FusionConfig';
import {useConfig} from "../context/ConfigProvider";
import {Anchor, Checkbox, Flexbox, Label, SearchableUserGridList, Section} from "../../common/fusion-ui";
import {Tooltip} from "@mui/material";

/**
 * Create share-page link panel/dialog/drawer component.
 *
 * @example
 * const [openShareDialog, setOpenShareDialog] = useState(false);
 * <EventReminderPanel
 *   moduleName={moduleName}
 *   recordId={recordId}
 *   recordUrl={`/internal-page?=${recordId}`}
 *   open={openShareDialog}
 *   recordData={{
 *       //recordData is optional
 *       title: "title",
 *       description: "description",
 *       date: "date",
 *       link: "link",
 *       selectedParticipants: [ { id: 55 }, { id: 56 } ],
 *   }}
 *   onHide={() => {
 *      setOpenShareDialog(false);
 *    }}
 *   onDoneAndHide={() => {
 *      setOpenShareDialog(false);
 *    }}
 *   onSucces={() => {
 *      setOpenShareDialog(false);
 *    }}
 * />
 */
const EventReminderPanel = React.memo(({
                                           moduleName,
                                           recordId,
                                           recordUrl,
                                           open,
                                           onHide,
                                           onDoneAndHide,
                                           showAs = ShowViewType.DIALOG,
                                           popupProps = {},
                                           recordData = {},
                                           onSuccess,
                                       }) => {
    const TAG = "EventReminderPanel";

    const theme = useTheme();
    const {showToast, changeToastVariant} = useToast();
    const {isConfigTrue, getConfig, updateConfig} = useConfig();

    const Fields = {
        isDataLoading: 'isDataLoading',
        isDataFound: 'isDataFound',
        dataLoadingMsg: 'dataLoadingMsg',
        isDataFetched: 'isDataFetched',

        submitBtn_AsLoading: 'submitBtn_AsLoading',
        submitBtn_disabled: 'submitBtn_disabled',

        canSendEmailReminders: 'canSendEmailReminders',
        canSendSmsReminders: 'canSendSmsReminders',
    };
    const [fieldsData, setFieldsData] = useState({
        // [Fields.isDataLoading]: true,
    });
    const [eventData, setEventData] = useState({});

    const [copyShareLinkPending, startCopyShareLinkTransition] = useTransition();

    const REMINDER_VIA_TYPES = [
        {field: Fields.canSendEmailReminders, via: `Email`},
        {field: Fields.canSendSmsReminders, via: `SMS`},
    ];

    useEffect(() => {
        if (isBoolTrue(open)) {
            fetchModuleEventData();
            if (!checkNullStr(getField(Fields.canSendEmailReminders))) {
                updateField(Fields.canSendEmailReminders, true);
                // updateField(Fields.canSendSmsReminders, true);//Not available in backend
                // log('useEffect.formData:', formData, 'fieldsData:', fieldsData, 'moduleName:', moduleName);
                log('useEffect.moduleName:', moduleName, 'recordId:', recordId, 'recordData:', recordData);
            }
        }
    }, [open, moduleName, recordId, recordData]);

    useEffect(() => {
    }, []);

    return (<>
        <InternalPageRoot
            tag={showAs == ShowViewType.PAGE ? TAG : getConfig(Keys.tracker_currPageTag)}
            moduleName={showAs == ShowViewType.PAGE ? moduleName : getConfig(Keys.tracker_currPageModule)}
            title={`Send Event Reminder`}
            // title={<span className={'fw-semi-bold fs-normal text-wrap select-none'}>
            //     <span>Send Event Reminder </span>
            // </span>}
            isDataFound={getField(Fields.isDataFound)}
            isDataLoading={getField(Fields.isDataLoading)}
            dataLoadingMsg={getField(Fields.dataLoadingMsg)}
            showHeader={false}
            closeOnEscape={true}
            closeOnOutsideClick={false}
            dialogMd={7}
            open={isBoolTrue(open)}
            onHide={handlePopupHide}
            showAs={showAs}
            popupProps={{
                titleAt: 'start',
                subtitleAt: 'start',
                subtitle: 'Send event-reminder to the participants via Email/SMS',
                titleClassName: `${showAs == ShowViewType.DRAWER ? 'mx-n2' : ''}`,
                subtitleClassName: 'mt-1x ',
                headerClassName: `${showAs == ShowViewType.DRAWER ? 'px-4' : ''}`,
                contentClassName: `pt-5 mt-2x px-3 mx-1 pb-0 ${showAs == ShowViewType.DRAWER ? 'mb-n3' : ''}`,
                minHeight: showAs == ShowViewType.DRAWER ? 'auto' : '75.5vh',
                subtitleStyle: {
                    color: theme.text70
                },
                headerStyle: {
                    // backgroundColor: theme.text10
                },
                ...popupProps,
            }}
        >
            {createPanel()}
        </InternalPageRoot>
    </>);

    function createPanel() {
        return <div className={'mt-n5'}>
            {/*body*/}
            <div className={'px-3x pb-2 mt-3x'} style={{color: theme.text}}>
                {/*<div>curr-module: {moduleName}</div>*/}
                {/*<div>recordId: {recordId}</div>*/}
                {/*event-details*/}
                <div className={'mt-2'}>
                    {/*event-title*/}
                    <div>
                        <Label className={'m-0 p-0 text-wrap'} textAt={'start'}>
                            <span className={'fw-bold'}>Event: </span>
                            {checkNullStr(getJsonValue(eventData, 'title')) ? <span>
                                <span className={''}>{getJsonValue(eventData, 'title')}</span>

                                {/*moduleName*/}
                                <span>&nbsp;
                                    <span className={'rounded-10 fs-md-sm py-1 fw-bold text-center-force text-nowrap'}
                                          style={{color: theme.primaryDark, backgroundColor: theme.primary10}}>
                                    &nbsp;&nbsp;{moduleName}&nbsp;&nbsp;
                                </span>
                            </span>
                            </span> : DefStrNA}
                        </Label>
                    </div>

                    {/*event-date*/}
                    {checkNullStr(getJsonValue(eventData, 'date')) && <div>
                        <Label className={'m-0 p-0'} textAt={'start'}>
                            <span className={'fw-bold'}>Date: </span>
                            <span className={'text-nowrap'}>{formatDateValue(getJsonValue(eventData, 'date'))}</span>
                        </Label>
                    </div>}

                    {/*event-start-date*/}
                    {checkNullStr(getJsonValue(eventData, 'startDate')) && <div>
                        <Label className={'m-0 p-0'} textAt={'start'}>
                            <span className={'fw-bold'}>Start Date: </span>
                            <span className={'text-nowrap'}>{formatDateValue(getJsonValue(eventData, 'startDate'))}</span>
                        </Label>
                    </div>}

                    {/*event-end-date*/}
                    {checkNullStr(getJsonValue(eventData, 'endDate')) && <div>
                        <Label className={'m-0 p-0'} textAt={'start'}>
                            <span className={'fw-bold'}>End Date: </span>
                            <span className={'text-nowrap'}>{formatDateValue(getJsonValue(eventData, 'endDate'))}</span>
                        </Label>
                    </div>}

                    {/*event-venue*/}
                    {checkNullStr(getJsonValue(eventData, 'venue')) && <div>
                        <Label className={'m-0 p-0 text-wrap'} textAt={'start'}>
                            <span className={'fw-bold'}>Venue: </span>
                            <span className={''}>{getJsonValue(eventData, 'venue')}</span>
                        </Label>
                    </div>}

                    {/*event-link*/}
                    {checkNullStr(getJsonValue(eventData, 'link')) &&
                        <Flexbox className={'m-0 p-0 '} justifyAt={'start'} alignAt={'start'}>
                            <Label className={'fw-bold text-nowrap'} textAt={'start'}>Event Link:&nbsp;</Label>
                            <Tooltip title={(getJsonValue(eventData, 'link') + '').length > 40 ? getJsonValue(eventData, 'link') : ''}>
                                <Anchor
                                    className={''}
                                    simple={true}
                                    target={"_blank"}
                                    href={getJsonValue(eventData, 'link')}>
                                    {limitStringWords(getJsonValue(eventData, 'link'), 40, '', '...')}
                                </Anchor>
                            </Tooltip>

                            {/*copy-btn*/}
                            <ThemedIconButton
                                className={'p-1 rounded-2'}
                                ml={1} mt={'n1'}
                                size={'30px'}
                                outlined={true}
                                variant={theme.primaryBlue}
                                borderColor={'transparent'}
                                disableVariant={'xl'}
                                icon={IconClass.copy}
                                tooltip={"Copy Link"}
                                circular={false}
                                loadingVariant={CssVariant.primary}
                                asLoading={copyShareLinkPending}
                                disabled={copyShareLinkPending}
                                onClick={() => {
                                    startCopyShareLinkTransition(() => {
                                        copyTextToClipboard(getJsonValue(eventData, 'link'));
                                        toast(`URL copied to clipboard`, CssVariant.info);
                                    });
                                }}
                            />
                        </Flexbox>}

                    {/*event-description*/}
                    {/*<div>*/}
                    {/*    <Label className={'m-0 p-0'} textAt={'start'}>*/}
                    {/*        <span className={'fw-bold'}>Description: </span>*/}
                    {/*        <span className={''}>{extractHtmlInnerText(getJsonValue(eventData, 'description'))}</span>*/}
                    {/*    </Label>*/}
                    {/*</div>*/}
                </div>

                {/*participant-list*/}
                <div className={'mt-2'}>
                    <Label className={'text-input-label m-0 p-0 px-1'} color={theme.text40} textAt={'start'}>Participants</Label>
                    <Section className={'p-0 pt-2'}
                             roundedVariant={3}
                             borderColor={theme.text30}>
                        <SearchableUserGridList
                            wrapperClassName={''}
                            scrollerClassName={'px-1'}
                            gridClassName={''}
                            itemClassName={''}
                            nameClassName={'fs-sm'}
                            emailClassName={'fs-md-sm mt-n1 fw-semi-bold'}
                            mobileClassName={'fs-md-sm mt-n1 fw-semi-bold'}
                            minHeight={'25vh'}
                            height={'25vh'}

                            itemSm={6}
                            itemMd={6}
                            itemGap={1}

                            itemNameKey={'name'}
                            // itemEmailKey={'email'}
                            itemEmailKey={'email_mobile'}
                            itemMobileKey={'mobile'}
                            itemUsernameKey={'username'}
                            itemImageKey={'image'}

                            imageSize={'30px'}
                            itemBg={'transparent'}
                            itemSelectedBg={theme.primaryBlue20}
                            itemHoverBg={theme.primaryBlue10}
                            itemSelectedHoverBg={theme.primaryBlue30}
                            nameColor={theme.text}
                            nameSelectedColor={theme.primaryBlueDark}
                            emailColor={theme.text70}
                            emailSelectedColor={theme.primaryBlueDark}
                            mobileColor={theme.text70}
                            mobileSelectedColor={theme.primaryBlueDark}

                            users={getJsonValue(eventData, 'participants', [])}
                            inlineItem={false}
                            showLoading={false}
                            showEmail={true}
                            showMobile={false}
                            listMsg={''}

                            onUsersSelected={(updatedList, selectedList) => {
                                // log('participantList.onUsersSelected.updatedList:', updatedList);
                                log('participantList.onUsersSelected.selectedList:', selectedList);
                                setEventData(prevState => ({
                                    ...prevState,
                                    selectedParticipants: selectedList
                                }));
                            }}
                        />
                    </Section>
                </div>

                {/*send-type*/}
                <div className={'mt-3'}>
                    <Label className={'text-input-label m-0 p-0 px-1'} color={theme.text} textAt={'start'}>Send Reminder Via</Label>
                    {REMINDER_VIA_TYPES.map((item, index) => {
                        return <div className={'d-flex'} key={index + item.field}>
                            <Checkbox
                                wrapperClassName={'w-auto d-flex'}
                                className={'my-1'}
                                py={0}
                                disabled={item.via == REMINDER_VIA_TYPES[1].via}
                                defaultChecked={isFieldTrue(item.field)}
                                checked={isFieldTrue(item.field)}
                                onChange={(e, value) => {
                                    updateField(item.field, value);
                                }}>
                                {item.via}
                            </Checkbox>
                        </div>
                    })}
                </div>
            </div>

            {/*footer*/}
            <div className={'my-3 position-sticky'} style={{bottom: '0', backgroundColor: theme.bg}}>
                <Flexbox alignAt={'center'} justifyAt={'end'} justifyAtSm={'start'}>
                    {/*cancel*/}
                    {(showAs == ShowViewType.DRAWER || showAs == ShowViewType.DIALOG)
                        && <ThemedButton
                            className={'text-nowrap'}
                            sleek={true}
                            text={'Cancel'}
                            variant={theme.text}
                            bgColor={theme.text10}
                            hoverBgColor={theme.text20}
                            color={theme.text}
                            hoverColor={theme.text}
                            rippleColor={theme.text30}
                            mx={2}
                            disabled={isFieldTrue(Fields.submitBtn_AsLoading)}
                            onClick={handlePopupHide}
                        />}

                    {/*submit*/}
                    <ThemedButton
                        className={'text-nowrap fw-semi-bold'}
                        sleek={true}
                        icon={'bi bi-bell'}
                        text={isFieldTrue(Fields.submitBtn_AsLoading) ? 'Sending Reminder...' : 'Send Reminder'}
                        variant={theme.primaryBlue}
                        hoverBgColor={theme.primaryBlueDark}
                        mx={2}
                        asLoading={isFieldTrue(Fields.submitBtn_AsLoading)}
                        disabled={isFieldTrue(Fields.submitBtn_disabled)}
                        // disabled={!isFieldTrue(Fields.canSendEmailReminders) && !isFieldTrue(Fields.canSendSmsReminders)}
                        // disableVariant={'xl'}
                        onClick={handleSubmit}
                    />
                </Flexbox>
            </div>
        </div>
    }

    function handlePopupHide() {
        setEventData({});
        setFieldsData({});
        if (onHide) onHide();
        if (onDoneAndHide) onDoneAndHide();
        // toast('Popup is hidden')
    }

    async function fetchModuleEventData(checkBefore = true) {
        const fun = 'fetchModuleEventData:';
        if (!checkBefore || (checkBefore && !isFieldTrue(Fields.isDataFetched))) {
            log(fun, 'moduleName:', moduleName, 'recordId:', recordId);
            updateField(Fields.isDataFound, true);
            updateField(Fields.isDataFetched, true);
            updateField(Fields.isDataLoading, true);
            updateField(Fields.dataLoadingMsg, '');
            setEventData({});

            const REQUEST_OPTIONS = {
                moduleName,
                recordId,
                participantsIds: checkNullArr(recordData.selectedParticipants)
                    ? dropArrEmptyValues(dropArrDuplicates(recordData.selectedParticipants.map(item => getJsonValue(item, 'id'))))
                    : [],
                companyData: getSubdomainSessionData(),
            };
            try {
                sendRequest({
                    reqUrl: ReqUrls.eventReminderAlert_fetchEventData,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);

                        let fetchedEvtData = getJsonValueFromNestedKeys(fetchedData, ['data', 'eventData'], {});
                        const success = isJsonValueTrue(fetchedData, 'success');
                        const message = getJsonValue(fetchedData, 'message');

                        if (success) {
                            // toast(message, CssVariant.success);
                            updateField(Fields.dataLoadingMsg, message);

                            if (checkNullJson(recordData)) {
                                setEventData({
                                    ...fetchedEvtData,
                                    ...recordData,
                                    selectedParticipants: fetchedEvtData.participants
                                });
                            } else {
                                setEventData({
                                    ...fetchedEvtData,
                                    selectedParticipants: fetchedEvtData.participants
                                });
                            }
                        } else {
                            performFailureTasks(null, message);
                        }
                        updateField(Fields.isDataFound, success);
                        updateField(Fields.isDataLoading, false);
                    }),
                    onError: ((error) => {
                        performFailureTasks(error, error.message)
                    }),
                });
            } catch (error) {
                performFailureTasks(error, error.message)
            }
        }

        function performFailureTasks(error, message = "Couldn't load event-data, please refresh again!") {
            toast(message, CssVariant.danger)
            if (error) logErr(fun, error);
            updateField(Fields.dataLoadingMsg, message);
            updateField(Fields.isDataFound, false);
            updateField(Fields.isDataLoading, false);
        }
    }

    function handleSubmit() {
        const fun = 'handleSubmit:';
        try {
            const dataToSend = getDataToSend();
            log(fun, 'dataToSend:', dataToSend);

            let allFieldsChecked = checkAllRequiredFields();
            // allFieldsChecked = false;//for checking
            if (allFieldsChecked) {
                updateField(Fields.submitBtn_AsLoading, true);
                updateField(Fields.submitBtn_disabled, true);

                const REQUEST_OPTIONS = {
                    formData: dataToSend,
                    // companyData: getSubdomainSessionData(),
                };
                sendRequest({
                    reqUrl: ReqUrls.eventReminderAlert_submitAlertData,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);

                        const message = getJsonValue(fetchedData, 'message');
                        const success = isJsonValueTrue(fetchedData, 'success');

                        if (success) {
                            toast(message, CssVariant.success);
                            handlePopupHide();
                            if (onSuccess) onSuccess();
                        } else {
                            performFailureTask(null, message);
                        }
                        updateField(Fields.submitBtn_disabled, false);
                        updateField(Fields.submitBtn_AsLoading, false);
                    }),
                    onError: ((error) => {
                        performFailureTask(error);
                    })
                });
            }
        } catch (error) {
            performFailureTask(error);
        }

        function performFailureTask(error, msg = "Share failed, please try again!") {
            if (checkNullStr(msg)) toast(msg, CssVariant.danger);
            updateField(Fields.submitBtn_AsLoading, false);
            updateField(Fields.submitBtn_disabled, false);
            if (error) logErr(fun, error);
        }
    }

    function checkAllRequiredFields() {
        let checked = false;
        let msg = '';
        if (!checkNullStr(recordId)) {
            msg = 'Record Id not found!';
        } else if (!checkNullStr(moduleName)) {
            msg = 'Module cannot be empty!';
        } else if (!checkNullStr(eventData.title)) {
            msg = 'Event cannot be empty!';
        } else if (!checkNullArr(eventData.selectedParticipants)) {
            msg = 'Participants not found!';
        } else if (!isFieldTrue(Fields.canSendEmailReminders) && !isFieldTrue(Fields.canSendSmsReminders)) {
            msg = "Please select at least one of type of reminder!";
        } else {
            checked = true;
        }
        if (!checked) {
            if (checkNullStr(msg)) toast(msg, CssVariant.warning);
        }
        return checked;
    }

    function getDataToSend() {
        return {
            recordId: recordId,
            moduleName: moduleName,
            recordUrl: `${getUrlMainDomain(window.location.href)}${recordUrl}`,
            ...eventData,
            sendEmail: isFieldTrue(Fields.canSendEmailReminders),
            sendSms: isFieldTrue(Fields.canSendSmsReminders),
        };
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});
export default EventReminderPanel;
